import React, { useEffect, useState } from "react";
import { ChevronDown, Mail, MessageSquare, Phone, RotateCcw, Sparkles, Star } from "lucide-react";
import { api } from "./api";
import { Badge, Bloc, Carte, Puces } from "./ui";

/* Tableau de bord → « Cours d'essai » (Jen, 01.11.2026 : « où je vois les résultats des avis ?
   un écran tableau de bord cours d'essai qui reprend les stats qu'on a déjà et rajoute les
   résultats », puis le soir : « le menu cours d'essai dans Élèves doit être déplacé dans le
   tableau de bord, avoir la section cours d'essai à lui »). Une section du tableau de bord :
   les chiffres des essais (à venir, eus, transformés en inscription, par cours, par mois), puis
   l'avis après l'essai : note, envie de continuer, raisons, dernières réponses. L'ancienne
   adresse « ?onglet=essais » mène à cette section (Console.jsx, ANCIENS). */

const PERIODES = [{ valeur: "30", label: "30 jours" }, { valeur: "90", label: "3 mois" }, { valeur: "365", label: "1 an" }];
const SUITES = { oui: ["Oui", "vert"], hesite: ["J'hésite", "ambre"], non: ["Non", "rouge"] };
const etoiles = (n) => (n ? "★".repeat(n) + "☆".repeat(5 - n) : "—");
const jourCourt = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");
const aller = (detail) => window.dispatchEvent(new CustomEvent("console-aller", { detail }));
// Les mots de l'avis, tels que la famille les voit (console/avis_essai.py : MOTS_NOTE, SUITES, RAISONS).
const MOTS_NOTE = ["Bof", "Moyen", "Bien", "Très bien", "Adoré"];
const SUITES_LONGUES = { oui: "Oui, je continue", hesite: "J'hésite encore", non: "Non" };
const RAISONS = { horaire: "Horaire", tarif: "Tarif", niveau: "Niveau / contenu du cours", prof: "Prof", ambiance: "Ambiance",
  distance: "Distance", autre_cours: "Je souhaite essayer un autre cours", autre: "Autre" };
const PAS_REPONDU = <span className="italic text-[var(--muted)]">pas répondu</span>;
const heureCourte = (iso) => {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  return t.toLocaleString("fr-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

/* Le détail d'un avis, question par question (Jen, 05.11.2026 : « on ne voit pas vraiment le
   détail de ce que les gens ont dit… plus d'info pour chaque question ? »). Chaque question
   de la page /avis-essai, la réponse ou « pas répondu » (la famille s'est arrêtée avant), où
   elle en est restée, et de quoi la rappeler. */
const DetailAvis = ({ x, onRemis }) => {
  const [efface, setEfface] = useState("");
  const qui = x.adulte ? "" : x.eleve_prenom;
  const raisons = (x.raisons || []).filter((r) => r !== "autre").map((r) => RAISONS[r] || r);
  if (x.autre) raisons.push(`autre : ${x.autre}`);
  const hesite = x.suite === "hesite" || x.suite === "non";
  const lignes = [
    [qui ? `Comment s'est passé le cours de ${qui} ?` : "Comment s'est passé le cours ?",
      x.note ? <><span className="tracking-wider" style={{ color: "#F59E0B" }}>{etoiles(x.note)}</span> <span className="text-[var(--muted)]">{MOTS_NOTE[x.note - 1]}</span></> : PAS_REPONDU],
    [qui ? `${qui} a envie de continuer ?` : "Envie de continuer ?", x.suite ? <strong>{SUITES_LONGUES[x.suite]}</strong> : PAS_REPONDU],
  ];
  if (hesite) {
    lignes.push([x.suite === "hesite" ? "Qu'est-ce qui fait hésiter ?" : "Qu'est-ce qui n'a pas convenu ?", raisons.length ? raisons.join(", ") : PAS_REPONDU]);
    lignes.push(["Rappel", x.rappel ? <strong>Oui, souhaite être rappelé·e</strong> : "Non"]);
  }
  lignes.push(["Un mot pour l'école", x.commentaire ? <span className="italic">« {x.commentaire} »</span> : x.suite ? <span className="text-[var(--muted)]">aucun</span> : PAS_REPONDU]);
  const ou = x.termine
    ? `Avis complet : « Envoyer » touché${x.maj_le ? ` le ${heureCourte(x.maj_le)}` : ""}.`
    : x.suite
      ? "Avis pas terminé : la famille n'a pas touché « Envoyer » (chaque réponse est gardée au fil des clics)."
      : "Seulement la note : la famille a touché une étoile dans l'e-mail, sans répondre à la suite.";
  const parent = `${x.prenom || ""} ${x.nom || ""}`.trim();
  const tel = String(x.telephone || "").trim();
  // Quelqu'un de l'école a touché le lien de la famille (Jen, 05.11.2026, Louca) : on efface
  // ses réponses, la famille peut toujours répondre avec son lien.
  const remettre = async () => {
    if (!window.confirm(`Effacer les réponses de cet avis (${qui || parent || "famille"}) ? La famille pourra toujours répondre avec son lien.`)) return;
    setEfface("…");
    try {
      await api(`/essais-avis/${encodeURIComponent(x.id)}/remettre-a-zero`, { method: "POST" });
      onRemis();
    } catch (e) { setEfface(e.message || "Pas effacé, réessaie."); }
  };
  return (
    <div className="mt-2 ml-0 md:ml-[4.75rem] rounded-xl p-3 text-[13px] space-y-2" style={{ background: "#F8FAFC", border: "1px solid var(--border-soft)" }}
      data-testid={`essais-avis-detail-${x.id}`}>
      <div className="text-xs text-[var(--muted)]">{ou}</div>
      <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,15rem)_1fr] gap-x-4 gap-y-1.5">
        {lignes.map(([q, r]) => (
          <React.Fragment key={q}>
            <dt className="text-[var(--muted)]">{q}</dt>
            <dd>{r}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div className="pt-2 border-t flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" style={{ borderColor: "var(--border-soft)" }}>
        <span className="text-[var(--muted)]">Essai du {jourCourt(x.date_essai)}{x.repondu_le ? ` · répondu le ${heureCourte(x.repondu_le)}` : ""}</span>
        {parent && <span><span className="text-[var(--muted)]">Fiche famille :</span> {parent}</span>}
        {tel && <a href={`tel:${tel.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 font-medium" style={{ color: "var(--primary-hover)" }}><Phone size={12} /> {tel}</a>}
        {x.email && <a href={`mailto:${x.email}`} className="inline-flex items-center gap-1 font-medium" style={{ color: "var(--primary-hover)" }}><Mail size={12} /> {x.email}</a>}
        <button type="button" onClick={remettre} disabled={efface === "…"} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-[var(--muted)] hover:text-[#B91C1C]"
          title="Pour un test fait avec le lien d'une famille : efface les réponses, la famille peut toujours répondre."
          data-testid={`essais-avis-remettre-${x.id}`}><RotateCcw size={12} /> Remettre à zéro</button>
        {efface && efface !== "…" && <span className="basis-full text-xs" style={{ color: "#B91C1C" }}>{efface}</span>}
      </div>
    </div>
  );
};

export default function SectionEssais() {
  const [jours, setJours] = useState("90");
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  // Les longues listes du bas : les 5 premières, « Voir les N autres » pour déplier (Jen,
  // 02.11.2026 : « la longue liste à la fin, afficher les 5 premiers et un bouton voir plus »).
  const [tousCours, setTousCours] = useState(false);
  const [tousAvis, setTousAvis] = useState(false);
  const [avisOuvert, setAvisOuvert] = useState(null);
  const [recharge, setRecharge] = useState(0);
  useEffect(() => { setD(null); api(`/essais/tableau?jours=${jours}`).then(setD).catch((e) => setErreur(e.message)); }, [jours, recharge]);
  const p = d?.periode;
  const a = d?.avis;
  return (
    <Bloc id="tdb-essais" Icon={Sparkles} ton="essais" titre="Cours d'essai" testid="section-essais"
      aide="Les essais réservés, ceux qui ont eu lieu, combien se sont inscrits ensuite, et ce que les familles en disent."
      action={<Puces valeur={jours} onChange={setJours} options={PERIODES} testid="essais-periode" />}>
      <div className="space-y-5" data-testid="console-essais">
      {!d ? <p className="text-[var(--muted)]">{erreur || "Chargement…"}</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="essais-chiffres">
            <Carte titre="À venir" valeur={d.a_venir} detail={`dont ${d.semaine} cette semaine`} />
            <Carte titre="Essais eus" valeur={d.passes - d.absents} detail={`${d.absents} absent${d.absents > 1 ? "s" : ""} · ${d.annules} annulé${d.annules > 1 ? "s" : ""}`} />
            <Carte titre="Transformés" valeur={`${p.taux_conversion} %`} ton="primaire"
              detail={`${p.essais_convertis} inscrits sur ${p.essais_eleves} · saison : ${d.saison.taux_conversion} %`} testid="essais-transformes" />
            <Carte titre="Note moyenne" valeur={a?.note_moyenne != null ? `${a.note_moyenne} ★` : "—"}
              detail={a ? `${a.reponses} avis sur ${a.envoyes} demandés (${a.taux} %)` : "l'avis après l'essai arrive bientôt"} testid="essais-note" />
            <Carte titre="Envie de continuer" valeur={a?.reponses ? `${Math.round((100 * a.oui) / a.reponses)} %` : "—"} ton="vert"
              detail={a?.reponses ? `oui ${a.oui} · j'hésite ${a.hesite} · non ${a.non}` : "pas encore de réponse"} />
            <Carte titre="À rappeler" valeur={a ? a.a_rappeler : "—"} ton={a?.a_rappeler ? "ambre" : "neutre"}
              detail="hésitent, disent non ou demandent un appel"
              onClick={a?.a_rappeler ? () => aller({ onglet: "messages" }) : undefined} testid="essais-a-rappeler" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panneau titre="Par mois" aide="Essais eus et élèves inscrits ensuite.">
              <div className="space-y-2" data-testid="essais-par-mois">
                {d.par_mois.map((m) => {
                  const max = Math.max(1, ...d.par_mois.map((x) => x.essais));
                  return (
                    <div key={m.mois} className="flex items-center gap-3 text-sm">
                      <span className="w-20 shrink-0 text-[var(--muted)]">{m.libelle}</span>
                      <span className="flex-1 h-5 rounded-full overflow-hidden relative" style={{ background: "#F1F5F9" }}>
                        <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(100 * m.essais) / max}%`, background: "#BFDBFE" }} />
                        <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(100 * m.convertis) / max}%`, background: "#2563EB" }} />
                      </span>
                      <span className="w-24 shrink-0 text-right text-xs"><strong>{m.convertis}</strong> / {m.essais}</span>
                    </div>
                  );
                })}
                <div className="text-xs text-[var(--muted)] flex gap-4 pt-1">
                  <span><span className="inline-block w-3 h-3 rounded-sm align-middle mr-1" style={{ background: "#2563EB" }} />inscrits</span>
                  <span><span className="inline-block w-3 h-3 rounded-sm align-middle mr-1" style={{ background: "#BFDBFE" }} />essais eus</span>
                </div>
              </div>
            </Panneau>
            <Panneau titre="Pourquoi ils hésitent" aide={a ? "Les raisons cochées par ceux qui hésitent ou disent non." : "Avec l'avis après l'essai (version de test pour l'instant)."}>
              {!a || !a.raisons.length ? <p className="text-sm text-[var(--muted)]">Pas encore de raison donnée.</p> : (
                <div className="space-y-2" data-testid="essais-raisons">
                  {a.raisons.map(([r, n]) => (
                    <div key={r} className="flex items-center gap-3 text-sm">
                      <span className="w-44 shrink-0">{r}</span>
                      <span className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: "#F1F5F9" }}>
                        <span className="block h-3 rounded-full" style={{ width: `${(100 * n) / a.raisons[0][1]}%`, background: "#F59E0B" }} />
                      </span>
                      <strong className="w-6 text-right">{n}</strong>
                    </div>
                  ))}
                </div>
              )}
            </Panneau>
          </div>

          <Panneau titre="Par cours" aide="Élèves venus en essai sur la période, inscrits ensuite (à ce cours ou à un autre), la note des avis.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="essais-par-cours">
                <thead><tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[var(--muted)] border-b" style={{ borderColor: "var(--border)" }}>
                  <th className="py-2 pr-3 font-semibold">Cours</th><th className="py-2 px-3 font-semibold text-right">Essais</th>
                  <th className="py-2 px-3 font-semibold text-right">Inscrits</th><th className="py-2 px-3 font-semibold text-right">Transformés</th>
                  <th className="py-2 pl-3 font-semibold text-right">Note</th></tr></thead>
                <tbody>
                  {(tousCours ? d.par_cours : d.par_cours.slice(0, PREMIERS)).map((c) => (
                    <tr key={c.cours_id} className="border-b last:border-b-0" style={{ borderColor: "var(--border-soft)" }}>
                      <td className="py-2 pr-3">{c.nom}</td>
                      <td className="py-2 px-3 text-right">{c.essais}</td>
                      <td className="py-2 px-3 text-right">{c.convertis}{c.meme_cours !== c.convertis ? <span className="text-xs text-[var(--muted)]"> ({c.meme_cours} à ce cours)</span> : null}</td>
                      <td className="py-2 px-3 text-right"><strong>{c.taux} %</strong></td>
                      <td className="py-2 pl-3 text-right whitespace-nowrap" style={{ color: "#F59E0B" }}>{c.note != null ? <>{c.note} ★ <span className="text-xs text-[var(--muted)]">({c.avis})</span></> : <span className="text-[var(--muted)]">—</span>}</td>
                    </tr>
                  ))}
                  {!d.par_cours.length && <tr><td colSpan={5} className="py-4 text-center text-[var(--muted)]">Aucun essai sur la période.</td></tr>}
                </tbody>
              </table>
            </div>
            <VoirPlus total={d.par_cours.length} ouvert={tousCours} onChange={setTousCours} testid="essais-par-cours-plus" />
          </Panneau>

          {a && (
            <Panneau titre="Les derniers avis" aide="Toucher un avis pour voir chaque question et sa réponse. Les « J'hésite », « Non » et demandes d'appel sont aussi dans Messages reçus, pour y répondre."
              action={<button className="btn-ghost text-xs" onClick={() => aller({ onglet: "messages" })}><MessageSquare size={12} /> Messages reçus</button>}>
              <div className="divide-y" style={{ borderColor: "var(--border-soft)" }} data-testid="essais-avis">
                {(tousAvis ? a.items : a.items.slice(0, PREMIERS)).map((x) => {
                  const [label, ton] = SUITES[x.suite] || ["—", "neutre"];
                  const ouvert = avisOuvert === x.id;
                  return (
                    <div key={x.id} className="py-2.5">
                    <button type="button" onClick={() => setAvisOuvert(ouvert ? null : x.id)} aria-expanded={ouvert}
                      className="w-full text-left flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm rounded-lg hover:bg-[var(--primary-tint)]"
                      data-testid={`essais-avis-ligne-${x.id}`}>
                      <span className="w-16 shrink-0 text-xs text-[var(--muted)]">{jourCourt(x.repondu_le)}</span>
                      <span className="tracking-wider" style={{ color: "#F59E0B" }}>{etoiles(x.note)}</span>
                      {x.suite && <Badge ton={ton}>{label}</Badge>}
                      <span className="font-medium">{x.adulte ? `${x.prenom || ""} ${x.nom || ""}`.trim() : x.eleve_prenom || x.prenom}</span>
                      <span className="text-[var(--muted)]">{x.cours_nom}</span>
                      {(x.raisons || []).length > 0 && <span className="text-xs">{x.resume.split(" · ").find((t) => /Horaire|Tarif|Niveau|Prof|Ambiance|Distance|autre/i.test(t)) || ""}</span>}
                      {x.rappel && <Badge ton="ambre">à rappeler</Badge>}
                      {!x.suite && <span className="text-xs text-[var(--muted)]">note seulement</span>}
                      <ChevronDown size={14} className="ml-auto self-center shrink-0 text-[var(--muted)] transition-transform" style={{ transform: ouvert ? "rotate(180deg)" : "none" }} />
                      {x.commentaire && !ouvert && <span className="basis-full pl-[4.75rem] text-xs italic text-[var(--muted)]">« {x.commentaire} »</span>}
                    </button>
                    {ouvert && <DetailAvis x={x} onRemis={() => { setAvisOuvert(null); setRecharge((n) => n + 1); }} />}
                    </div>
                  );
                })}
                {!a.items.length && <p className="text-sm text-[var(--muted)] py-3">Pas encore d'avis sur la période.</p>}
              </div>
              <VoirPlus total={a.items.length} ouvert={tousAvis} onChange={setTousAvis} testid="essais-avis-plus" />
            </Panneau>
          )}
          {!a && (
            <p className="text-sm text-[var(--muted)] flex items-center gap-2"><Star size={14} /> L'avis après le cours d'essai (étoiles, envie de continuer, raisons) arrive ici dès qu'il est ouvert.</p>
          )}
        </>
      )}
      </div>
    </Bloc>
  );
}

const PREMIERS = 5;
const VoirPlus = ({ total, ouvert, onChange, testid }) => (total > PREMIERS ? (
  <button type="button" onClick={() => onChange(!ouvert)} data-testid={testid}
    className="mt-2 w-full rounded-xl py-2 text-sm font-medium hover:bg-[var(--primary-tint)]" style={{ color: "var(--primary-hover)" }}>
    {ouvert ? "Réduire" : `Voir les ${total - PREMIERS} autres`}
  </button>
) : null);

const Panneau = ({ titre, aide, action, children }) => (
  <section className="bg-white rounded-2xl border p-4 md:p-5" style={{ borderColor: "var(--border)" }}>
    <div className="flex items-start gap-3 mb-3">
      <div className="min-w-0">
        <div className="font-semibold">{titre}</div>
        {aide && <p className="text-xs text-[var(--muted)] mt-0.5">{aide}</p>}
      </div>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </section>
);
